import { createContext, useContext, type ReactNode } from 'react';
import type {
  PublicClinicSummary,
  PublicInvestigatorDetail,
} from '@rudi/shared/types/clinical-trials';
import type { createClinicalOperations } from './operations';
import type { ClinicalTranslator } from './text';

interface RequestOptions {
  params?: Record<string, unknown>;
  responseType?: 'blob';
  timeout?: number;
}
export interface ClinicalEditorApi {
  get<T>(path: string, options?: RequestOptions): Promise<{ data: T }>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<{ data: T }>;
  put<T>(path: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(path: string, body?: unknown): Promise<{ data: T }>;
  delete(path: string): Promise<unknown>;
}
export interface ClinicalEditors {
  api: ClinicalEditorApi;
  t: ClinicalTranslator;
  profileOrigin: string;
  getPublicInvestigator: (slug: string) => Promise<PublicInvestigatorDetail>;
  listPublicClinics: () => Promise<PublicClinicSummary[]>;
  operations: ReturnType<typeof createClinicalOperations>;
  toast: (message: {
    title: string;
    description?: string;
    variant?: 'destructive' | 'default';
  }) => void;
}
const Context = createContext<ClinicalEditors | null>(null);
export function ClinicalEditorsProvider({
  value,
  children,
}: {
  value: ClinicalEditors;
  children: ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useClinicalEditors(): ClinicalEditors {
  const context = useContext(Context);
  if (!context) throw new Error('Clinical editors require an app transport.');
  return context;
}
export function getErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { detail?: unknown; error?: unknown } } })?.response
    ?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.error === 'string') return data.error;
  return error instanceof Error ? error.message : 'Could not save changes.';
}
